import { Box, Flex, xcss } from '@atlaskit/primitives';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { useLocale } from '@databases/intl';
import { abortDatabaseStream } from '@databases/rovo-bridge';
import React, { useCallback, useEffect, useRef } from 'react';
import { IntlProvider } from 'react-intl';

import { useDatabaseStreamState } from '../useDatabaseStreamState.ts';

const fixedThinkingBarContainerStyles = xcss({
  position: 'fixed',
  bottom: '36px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '400px',
  minWidth: 'fit-content',
  zIndex: 'modal'
});

const inlineThinkingBarContainerStyles = xcss({
  marginInline: 'auto',
  width: 'min(400px, 100%)'
});

interface DatabaseBoeThinkingBarProps {
  contentId: string;
  onDismiss: () => void;
  onStreamComplete?: () => void;
  placement?: 'fixed' | 'inline';
}

export const DatabaseBoeThinkingBar = ({
  contentId,
  onDismiss,
  onStreamComplete,
  placement = 'fixed'
}: DatabaseBoeThinkingBarProps) => {
  const locale = useLocale();
  const { isStreaming } = useDatabaseStreamState();

  // Track whether isStreaming has been true at least once since mount.
  // This prevents the bar from self-dismissing immediately on mount because
  // isStreaming starts as false and only transitions true → false when the
  // AI editing phase starts and then completes.
  const hasSeenStreamingRef = useRef(false);

  useEffect(() => {
    if (isStreaming) {
      hasSeenStreamingRef.current = true;
    } else if (hasSeenStreamingRef.current) {
      // isStreaming went false after having been true — stream is complete.
      onStreamComplete?.() ?? onDismiss();
    }
  }, [isStreaming, onDismiss, onStreamComplete]);

  // analyzing = prompt submitted, AI is researching (before streaming starts)
  // editing = AI is generating/populating the database
  const status = isStreaming ? 'editing' : 'analyzing';

  const handleStop = useCallback(() => {
    abortDatabaseStream(contentId);
    onDismiss();
  }, [contentId, onDismiss]);

  // RovoThinkingBar uses `useIntl` from `react-intl` (v7), whereas Databases
  // wraps its UI in react-intl-next (v5 alias). These are different React
  // contexts, so we provide a react-intl IntlProvider here.
  return (
    <IntlProvider locale={locale}>
      <Box
        xcss={
          placement === 'fixed'
            ? fixedThinkingBarContainerStyles
            : inlineThinkingBarContainerStyles
        }
        testId="database-boe-thinking-bar"
      >
        <RovoThinkingBarWrapper isThinking>
          <Flex alignItems="center" justifyContent="space-between">
            <RovoThinkingBar
              status={status}
              onStop={handleStop}
              showProgressBar
              useRotatingTitle
            />
          </Flex>
        </RovoThinkingBarWrapper>
      </Box>
    </IntlProvider>
  );
};
